import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Instagram } from "lucide-react";
import TickerStrip from "@/components/TickerStrip";
import WorkGallery from "@/components/WorkGallery";
import { useLanguage } from "@/context/LanguageContext";

export default function AboutPage() {
    const { t } = useLanguage();
    const { hash } = useLocation();
    const VALUES = t("about.values");

    useEffect(() => {
        if (hash) {
            setTimeout(() => {
                document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
            }, 400);
        }
    }, [hash]);

    return (
        <main className="pt-16 md:pt-20" data-testid="about-page">
            <div className="px-5 md:px-10 py-16 md:py-24">
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="font-display text-5xl md:text-7xl font-extrabold uppercase tracking-tighter"
                >
                    {t("about.title")}
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="mt-6 text-sm text-zinc-500 max-w-xl leading-relaxed"
                >
                    {t("about.body")}
                </motion.p>
            </div>

            <TickerStrip inverted />

            <div className="px-5 md:px-10 py-20 grid grid-cols-1 md:grid-cols-3 gap-10">
                {VALUES.map((v, i) => (
                    <motion.div
                        key={v.title}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.12, duration: 0.6 }}
                        className="border-t-2 border-black pt-6"
                        data-testid={`value-${i}`}
                    >
                        <span className="text-xs tracking-[0.3em] text-zinc-400">0{i + 1}</span>
                        <h3 className="font-display text-2xl font-bold uppercase mt-3">{v.title}</h3>
                        <p className="mt-4 text-sm text-zinc-600 leading-relaxed">{v.text}</p>
                    </motion.div>
                ))}
            </div>

            <WorkGallery />

            <div className="bg-zinc-100 px-5 md:px-10 py-20 text-center">
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight"
                >
                    {t("about.ctaTitle")}
                </motion.h2>
                <div className="mt-8 flex flex-wrap justify-center gap-4">
                    <Link
                        to="/shop"
                        data-testid="about-cta-shop"
                        className="group bg-black text-white px-8 py-4 text-xs tracking-[0.25em] uppercase font-semibold flex items-center gap-3 transition-colors duration-300 hover:bg-zinc-800"
                    >
                        {t("about.ctaShop")}
                        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                    </Link>
                    <a
                        href="https://instagram.com/seva_wear"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="about-cta-instagram"
                        className="border border-black px-8 py-4 text-xs tracking-[0.25em] uppercase font-semibold flex items-center gap-3 transition-colors duration-300 hover:bg-black hover:text-white"
                    >
                        <Instagram size={16} />
                        Instagram
                    </a>
                </div>
            </div>
        </main>
    );
}
